import { useTextField } from '@react-aria/textfield';
import { forwardRef, ReactNode, RefObject, useRef } from 'react';
import { useMergeRefs } from '../../hooks/useMergeRefs';
import { Box } from '../Box';
import { Flex } from '../Flex';
import { Label } from '../Label';
import { Text } from '../Text';
import { DatePicker, DatePickerProps } from '.';

export interface DatePickerFieldFieldProps extends DatePickerProps {
  /** Input helper text */
  description?: string;
  /** Input label text */
  label?: ReactNode;
  /** Input element css */
  inputCss?: DatePickerProps['css'];
}

export const DatePickerField = forwardRef<
  HTMLButtonElement,
  DatePickerFieldFieldProps
>(
  (
    {
      disabled,
      readOnly,
      required,
      leftIcon,
      rightIcon,
      error,
      description,
      label,
      css,
      size,
      inputCss,
      ...rest
    },
    ref,
  ) => {
    const internalRef = useRef<HTMLButtonElement>();
    const combinedRef = useMergeRefs(internalRef, ref);

    const { labelProps, errorMessageProps, descriptionProps } = useTextField(
      {
        label,
        isDisabled: disabled,
        isReadOnly: readOnly,
        isRequired: required,
        validationState: error ? 'invalid' : 'valid',
      },
      internalRef as RefObject<HTMLInputElement>,
    );

    return (
      <Box css={css}>
        {label && typeof label === 'string' ? (
          <Label
            css={{
              mb: 8,
            }}
            {...labelProps}
            required={required}
          >
            {label}
          </Label>
        ) : (
          label
        )}
        <DatePicker
          aria-labelledby={labelProps?.id}
          css={inputCss}
          ref={combinedRef}
          size={size}
          readOnly={readOnly}
          disabled={disabled}
          required={required}
          leftIcon={leftIcon}
          rightIcon={rightIcon}
          error={error}
          {...rest}
        />
        <Flex
          className='betterleap-ui-input-error'
          css={{ minHeight: 20 }}
          align='end'
        >
          {description && !error && (
            <Text
              css={{
                color: '$text-lightest',
                fontSize: '$sm',
              }}
              noBreak
              {...descriptionProps}
            >
              {description}
            </Text>
          )}
          {error && (
            <Text
              css={{
                color: '$text-danger',
                fontSize: '$sm',
              }}
              noBreak
              {...errorMessageProps}
            >
              {error}
            </Text>
          )}
        </Flex>
      </Box>
    );
  },
);

DatePickerField.displayName = 'DatePickerField';
